import * as React from 'react';
import { withRouter, Link, RouteComponentProps } from 'react-router-dom';
import { Button, Card, Col, Divider, Modal, notification, Row, Table, Input } from 'antd';

import CountriesQuery from '../../components/Query/Countries';
import CountryDetail from './CountryDetail';
import { Title } from '../../components/Layout/components';
import NotFoundPage from '../NotFound/NotFound';

interface ICountriesTableProps extends RouteComponentProps {
  domain: string;
  countryId?: string;
}

interface ICountriesTableState {
  searchQuery: string;
}

class CountriesTable extends React.Component<ICountriesTableProps, ICountriesTableState> {

  constructor(props: any) {
    super(props);

    this.state = {
      searchQuery: '',
    };
  }

  public componentDidMount() {
    if ((this.props as any).onCollapse) {
      (this.props as any).onCollapse(false);
    }
  }

  public render() {
    return (
      <Row>
        <Col span={24}>
          <CountriesQuery
            search={this.state.searchQuery}
          >
            {({ countries, createCountry, updateCountry, deleteCountry }: any) => {
              return this.renderData(countries, createCountry, updateCountry, deleteCountry, countries.refetch);
            }}
          </CountriesQuery>
        </Col>
      </Row>
    );
  }

  private renderData(
    countries: any,
    createCountry: Function,
    updateCountry: Function,
    deleteCountry: Function,
    refetch: Function
  ) {
    if (countries && !countries.loading) {

      // formulář
      if (this.props && this.props.countryId) {
        const selectedCountry = countries.countries.find((s: GQL.ICountry) => s.id === this.props.countryId) || undefined;
        if (this.props.countryId !== 'new' && !selectedCountry) {
          return <NotFoundPage domain={this.props.domain} />
        }

        return (
          <CountryDetail
            domain={this.props.domain}
            languages={countries.languages}
            country={countries.countries.find((s: GQL.ICountry) => s.id === this.props.countryId) || undefined}
            onSave={a => this.upsert(a, createCountry, updateCountry, refetch)}
            onCancel={() => this.cancel()}
          />
        );
      }

      return (
        <div>
          <Title>Countries</Title>
          <Divider />
          <Row>
            <Col span={12}>
              {/* <Link className="ant-btn" to={this.getLink('new')}>
                {`Create new country`}
              </Link> */}
            </Col>
            <Col span={12}>
              <Input.Search
                placeholder="Search countries..."
                defaultValue={this.state.searchQuery}
                onSearch={query => this.changeSearchQuery(query)}
                enterButton={true}
              />
            </Col>
          </Row>
          <Table
            rowKey={(record: GQL.ICountry) => record.id}
            dataSource={countries.countries}
            defaultExpandAllRows={true}
            style={{ marginTop: '20px' }}
          >
            <Table.Column
              title="Code"
              dataIndex="code"
              key="code"
              width={80}
              align="center"
            />
            <Table.Column
              title="Name"
              dataIndex="name"
              key="name"
            />
            <Table.Column
              title="Business"
              dataIndex="business"
              key="business"
              render={(u: any, row: GQL.ICountry) => {
                if (row.business) {
                  return row.business.name;
                }
                return `-`;
              }}
            />
            <Table.Column
              title="Vat rate"
              dataIndex="VATrate"
              key="VATrate"
              width={90}
              align="right"
              render={(u: any, row: GQL.ICountry) => {
                if (row.VATrate === 0) {
                  return '-';
                }
                return `${row.VATrate} %`;
              }}
            />
            <Table.Column
              title="Language"
              dataIndex="language"
              key="language"
              width={120}
              align="center"
              render={(u: any, row: GQL.ICountry) => {
                if (row && row.language && row.language.value) {
                  return row.language.value;
                }
                return '-';
              }}
            />
            <Table.Column
              title="Currency"
              dataIndex="currency"
              key="currency"
              width={120}
              align="center"
              render={(u: any, row: GQL.ICountry) => {
                if (row && row.currency && row.currency.value) {
                  return row.currency.value;
                }
                return '-';
              }}
            />
            <Table.Column
              width={180}
              title="Actions"
              dataIndex="actions"
              key="actions"
              align="center"
              render={(u: any, row: GQL.ICountry) => this.renderActions(u, row, deleteCountry, refetch, countries.countries)}
            />
          </Table>
        </div>
      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading countries ..." style={{ width: '100%' }} />
      </div>
    );
  }

  private renderActions(
    u: any,
    country: GQL.ICountry,
    deleteCountry: Function,
    refetch: Function,
    allCountries: Array<GQL.ICountry>
  ) {
    const childCountries = allCountries 
      && allCountries.filter((c: GQL.ICountry) => c.parentCountryId === country.id);

    return (
      <div>
        <Link className="ant-btn" type={'dashed'} to={this.getLink(country.id)}>
          Edit
        </Link>
        <Divider type={'vertical'} />
        <Button danger={true} disabled={childCountries.length > 0} onClick={() => this.delete(country, deleteCountry, refetch)}>
          Delete
        </Button>
      </div>
    );
  }

  private changeSearchQuery(searchQuery: string) {
    this.setState({
      searchQuery
    });
  }

  private delete(country: GQL.ICountry, deleteCountry: Function, refetch: Function) {
    Modal.confirm({
      title: `Delete ${country.name}?`,
      content: (
        <span>
          Are you sure you want to delete country <strong>{country.name}</strong>
        </span>
      ),
      onOk: () => {
        return deleteCountry({
          variables: {
            id: country.id
          }
        }).then(() => refetch());
      }
    });
  }

  private upsert(
    country: GQL.ICountry,
    createCountry: Function,
    updateCountry: Function,
    refetch: Function
  ) {
    if (country != undefined) {
      const variables = {
        id: country.id,
        country: {
          name: country.name,
          code: country.code,
          VATrate: country.VATrate || 0,
          currencyId: country.currencyId,
          languageId: country.languageId,
          businessId: (country as any).businessId,
          weightUnit: country.weightUnit,
          bankName: country.bankName,
          iban: country.iban,
          swift: country.swift,
          bankAccountNumber: country.bankAccountNumber,
          states: country.states && country.states.map((countryState: GQL.ICountryState) => ({
            name: countryState.name,
            title: countryState.title,
            vatRate: countryState.vatRate,
          })) || null,
        }
      };

      if (country.id) {
        return updateCountry({ variables })
          .then(() => refetch())
          .then(() => this.cancel())
          .then(() =>
            notification['success']({
              message: 'Info',
              description: `Country "${variables.country.name}" has been updated.`
            })
          );
      }

      createCountry({ variables })
        .then(() => refetch())
        .then(() => this.cancel())
        .then(() =>
          notification['success']({
            message: 'Info',
            description: `Country "${variables.country.name}" has been created.`
          })
        );
    }
  }

  private getLink(recordId: string | null) {
    return `/${this.props.domain}/application/${recordId ? `countries/${recordId}` : 'countries'}`;
  }

  private cancel() {
    this.props.history.push(this.getLink(null));
  }
}

export default withRouter(CountriesTable);


// WEBPACK FOOTER //
// ./src/scenes/Countries/CountriesTable.tsx